<template>
	<div>
		<!-- Multi mode: list of current files + add more -->
		<div v-if="multiple">
			<div v-for="(file, i) in value" :key="i" class="mb-1">
				<div class="d-flex align-items-center">
					<b-avatar rounded button @click="playPauseAudio(i)" size="2rem"
						:src="file.mime && file.mime[0] === 'image' && file.url || null"
						:icon="file.mime && (file.mime[0] === 'audio' || file.mime[0] === 'video' && file.mime[1] !== 'gif') && (playing === i ? 'stop' : 'play') || ''"
						class="mr-2 flex-shrink-0"
					></b-avatar>
					<span class="flex-fill text-truncate small">{{file.filename}} ({{bytesString(file.size)}}B)</span>
					<b-button size="sm" variant="danger" @click="removeFile(i)" class="ml-1 flex-shrink-0"><b-icon icon="x"></b-icon></b-button>
				</div>
				<b-progress v-if="file._progress != null" :value="file._progress" max="100" height="3px" class="mt-1"></b-progress>
				<div v-if="file._error" class="small text-danger mt-1">⚠ {{file._error}}</div>
			</div>
			<b-form-file @change="onChange" :accept="accept" multiple class="mt-1"></b-form-file>
		</div>
		<!-- Single mode -->
		<b-form-row v-else>
			<b-col cols="2" @click="playPauseAudio(0)">
				<b-avatar rounded size="4rem"
					:src="value && value.md5 && value.mime && value.mime[0] === 'image' && value.url || null"
					:icon="value && value.mime && value.mime[0] === 'audio' && (playing === 0 ? 'stop' : 'play') || ''"
				></b-avatar>
			</b-col>
			<b-col>
				<b-form-file @change="onChange" :accept="accept"></b-form-file>
				<div v-if="singleProgress != null" class="d-flex align-items-center mt-1">
					<b-progress :value="singleProgress" max="100" height="3px" class="flex-fill mr-1"></b-progress>
					<b-button size="sm" variant="link" class="text-danger p-0" @click="clearSingle()"><b-icon icon="x-circle"></b-icon></b-button>
				</div>
				<div v-else-if="singleError" class="small text-danger mt-1">⚠ {{singleError}}</div>
				<div v-else-if="value && value.md5" class="d-flex align-items-center mt-1">
					<span class="small text-muted flex-fill">{{value.filename}} ({{bytesString(value.size)}}B)</span>
					<b-button size="sm" variant="link" class="text-danger p-0 ml-1" @click="clearSingle()"><b-icon icon="x-circle"></b-icon></b-button>
				</div>
			</b-col>
		</b-form-row>
	</div>
</template>

<script>
import util from '@/util.js';

export default {
	name: 'PipiPupuFile',
	props: {
		accept: String,
		value: [Object, Array],
		multiple: { type: Boolean, default: false },
	},
	data() { return {
		playing: -1,
		audio: null,
		singleProgress: null,
		singleError: null,
		uploading: 0,
	}},
	created() {
		this._abortControllers = new Set();
	},
	mounted() {
		this.$root.$on('bv::modal::hide', this._onModalHide);
	},
	beforeDestroy() {
		this.$root.$off('bv::modal::hide', this._onModalHide);
		this._clearAudio();
		this._cancelAll();
	},
	methods: {
		waitForUploads() {
			if(this.uploading === 0) return Promise.resolve();
			return new Promise(resolve => {
				const unwatch = this.$watch('uploading', val => {
					if(val === 0) { unwatch(); resolve(); }
				});
			});
		},
		onChange($event) {
			this._clearAudio();
			const files = $event.target.files;
			if(!files?.length) return;

			if(this.multiple) {
				for(const file of files) {
					const controller = new AbortController();
					const placeholder = { filename: file.name, size: file.size, _progress: 0, _abort: controller };
					this._abortControllers.add(controller);
					this.value.push(placeholder);
					this.$emit('input', this.value);
					this.uploading++;
					this.$api.uploadFileWithProgress(file.name, file, p => {
						this.$set(placeholder, '_progress', p);
					}, controller.signal).then(result => {
						this.$set(placeholder, 'md5', result.md5);
						this.$set(placeholder, 'mime', result.mime);
						this.$set(placeholder, 'url', result.url);
						this.$set(placeholder, '_progress', null);
						this.$emit('input', this.value);
					}).catch(err => {
						this.$set(placeholder, '_progress', null);
						if(!controller.signal.aborted)
							this.$set(placeholder, '_error', err.message || 'Upload failed');
					}).finally(() => {
						this.$delete(placeholder, '_abort');
						this._abortControllers.delete(controller);
						this.uploading--;
					});
				}
			} else {
				this.$emit('input', null);
				this.singleError = null;
				const controller = new AbortController();
				this._singleAbort = controller;
				this._abortControllers.add(controller);
				this.singleProgress = 0;
				this.uploading++;
				this.$api.uploadFileWithProgress(files[0].name, files[0], p => {
					this.singleProgress = p;
				}, controller.signal).then(result => {
					this.$emit('input', result);
				}).catch(err => {
					if(!controller.signal.aborted)
						this.singleError = err.message || 'Upload failed';
				}).finally(() => {
					this._singleAbort = null;
					this.singleProgress = null;
					this._abortControllers.delete(controller);
					this.uploading--;
				});
			}
		},

		removeFile(i) {
			const file = this.value[i];
			if(file._abort) file._abort.abort();
			if(i === this.playing) this._clearAudio();
			else if(i < this.playing) this.playing--;
			this.value.splice(i, 1);
		},

		clearSingle() {
			if(this._singleAbort) this._singleAbort.abort();
			this.singleProgress = null;
			this.singleError = null;
			this._clearAudio();
			this.$emit('input', null);
		},

		_cancelAll() {
			for(const ctrl of this._abortControllers) ctrl.abort();
		},

		_onModalHide(bvEvent) {
			let p = this.$parent;
			while(p) {
				if(p === bvEvent.vueTarget) { this._cancelAll(); return; }
				p = p.$parent;
			}
		},

		playPauseAudio(i = 0) {
			const file = this.multiple ? this.value?.[i] : this.value;
			if(!file?.url) return;
			const mime0 = file.mime?.[0];
			if(mime0 !== 'audio' && !(mime0 === 'video' && file.mime?.[1] !== 'gif')) return;

			if(this.audio) {
				const wasPlaying = this.playing;
				this._clearAudio();
				if(wasPlaying === i) return;
			}

			this.playing = i;
			const audio = this.audio = new Audio(file.url);
			const done = () => { if(this.audio === audio) this._clearAudio(); };
			audio.addEventListener('ended', done);
			audio.addEventListener('pause', done);
			audio.addEventListener('canplaythrough', () => audio.play());
		},

		_clearAudio() {
			this.playing = -1;
			const audio = this.audio;
			this.audio = null;
			if(audio) audio.pause();
		},

		bytesString(n) { return util.bytesString(n); },
	},
};
</script>
